import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { paymentClient } from '../../api'
import { clearCheckoutState, describeCheckResult, storedCheckoutSessionId, storedReturnPath, type PaymentFeedback } from './paymentFlow'
import { PaymentNotice, PaymentPage } from './paymentShared'

/**
 * `/payment/success` — Stripe Checkout's success return (legacy lib
 * `PaymentSuccessComponent`). Calls `PATCH /payment/sessions/{id}/check`
 * EXACTLY ONCE per visit for the session this tab started (the backend
 * books a completed session once no matter how often it is checked, but
 * the page must not rely on that), then shows what the answer means — only
 * a complete, settled session is shown as success — with a link back to the
 * same-site page the payment started from.
 *
 * Deliberate difference: legacy redirected immediately after the check,
 * whatever it answered, so a failed payment looked the same as a paid one.
 */
export function PaymentSuccessPage() {
  const started = useRef(false)
  const [state, setState] = useState<{ feedback: PaymentFeedback; returnPath: string } | null>(null)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const sessionId = storedCheckoutSessionId()
    const returnPath = storedReturnPath()
    if (!sessionId) {
      setState({ feedback: { tone: 'info', text: 'There is no checkout in progress in this browser tab.' }, returnPath })
      return
    }
    paymentClient
      .checkSession(sessionId)
      .then((result) => {
        clearCheckoutState()
        paymentClient.clearCache()
        setState({ feedback: describeCheckResult(result), returnPath })
      })
      .catch(() => {
        // The session id is kept: a reload of this page is a new visit that
        // checks again. Never shown as success.
        setState({ feedback: describeCheckResult(null), returnPath })
      })
  }, [])

  return (
    <PaymentPage title="Payment">
      <section className="portal-card" aria-label="Payment result">
        {!state ? (
          <p className="portal-notice portal-notice--info" role="status">
            <strong>Processing payment.</strong> Please don&apos;t close this window.
          </p>
        ) : (
          <>
            <PaymentNotice feedback={state.feedback} />
            <p>
              <Link to={state.returnPath}>Continue</Link> · <Link to="/payment/transactions">Transactions</Link>
            </p>
          </>
        )}
      </section>
    </PaymentPage>
  )
}
